import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import { lookup } from "@repo/vihangam-yog-shared";
import type { inferRouterOutputs } from "@trpc/server";

import { CHAPTER } from "../../chapter";

export type EventGroup =
  inferRouterOutputs<AppRouter>["adminListEventRegistrations"]["events"][number];
export type Registration = EventGroup["registrations"][number];
export type PartyMember = Registration["party"][number];

/** An infant is under two; a senior is sixty or over (the form's copy). */
const INFANT_MAX_AGE = 2;
const SENIOR_MIN_AGE = 60;
/** The Singapore organisers place a family's Kund by how many under-10s
 *  are coming, so the strip counts them separately from infants. */
const UNDER_TEN_MAX_AGE = 10;

/** The tags the door and the seating plan read off one party member:
 *  the needs they ticked, plus Infant / Senior derived from age. */
export function partyTags(member: PartyMember): string[] {
  const tags: string[] = [];
  for (const need of member.needs ?? []) {
    tags.push(need === "wheelchair" ? "Wheelchair" : "Needs a chair");
  }
  if (member.age !== undefined) {
    if (member.age < INFANT_MAX_AGE) {
      tags.push("Infant");
    } else if (member.age >= SENIOR_MIN_AGE) {
      tags.push("Senior");
    }
  }
  return tags;
}

/** "F" or "M" for the roster and the door; nothing for "other" (a
 *  preference not to say) or when the form did not ask. */
function genderMark(member: PartyMember): string | null {
  switch (member.gender) {
    case "female":
      return "F";
    case "male":
      return "M";
    default:
      return null;
  }
}

/** "Asha (64, F)", "Ava (6)", "Ravi (M)" or just the name: one party
 *  member as the roster and the door card print them. */
export function partyMemberLabel(member: PartyMember): string {
  const inBrackets: string[] = [];
  if (member.age !== undefined) {
    inBrackets.push(String(member.age));
  }
  const mark = genderMark(member);
  if (mark) {
    inBrackets.push(mark);
  }
  return inBrackets.length === 0
    ? member.name
    : `${member.name} (${inBrackets.join(", ")})`;
}

/** Seating numbers the organisers asked for, summed over a roster. */
export interface AccessCounts {
  wheelchair: number;
  chair: number;
  infants: number;
  /** Everyone under ten, infants included. */
  underTen: number;
  seniors: number;
  /** People recorded as female / male; those who did not say are in neither. */
  women: number;
  men: number;
}

export function accessCountsOf(
  regs: readonly Pick<Registration, "party">[],
): AccessCounts {
  const counts: AccessCounts = {
    wheelchair: 0,
    chair: 0,
    infants: 0,
    underTen: 0,
    seniors: 0,
    women: 0,
    men: 0,
  };
  for (const r of regs) {
    for (const m of r.party) {
      if (m.age !== undefined && m.age < UNDER_TEN_MAX_AGE) {
        counts.underTen += 1;
      }
      if (m.gender === "female") {
        counts.women += 1;
      } else if (m.gender === "male") {
        counts.men += 1;
      }
      for (const tag of partyTags(m)) {
        if (tag === "Wheelchair") {
          counts.wheelchair += 1;
        } else if (tag === "Needs a chair") {
          counts.chair += 1;
        } else if (tag === "Infant") {
          counts.infants += 1;
        } else if (tag === "Senior") {
          counts.seniors += 1;
        }
      }
    }
  }
  return counts;
}

/** A row in the roster view — a registration tagged with its event's
 *  title so the flattened "All events" table can show where each came
 *  from. */
export type RosterRow = Registration & { eventTitle: string };

/** Rows registered within this window carry the "new" amber tick. */
export const NEW_WINDOW_MS = 48 * 60 * 60 * 1000;

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Display label for a stored "heard about us" value. Mirrors the
 *  shared HEARD_ABOUT_US_OPTIONS labels. The dash for "nothing recorded"
 *  is what the guest list download writes, so it stays; on screen the
 *  guest list shows "Not given" instead. */
const SOURCE_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  friend: "A friend",
  family: "Family",
  member: "Already a member",
  past_event: "Past event",
  other: "Other",
} satisfies Record<string, string>;
/** "A friend: Priya Nair" when the registrant named who told them;
 *  the bare channel otherwise, and a dash when nothing was recorded. */
export function sourceLabel(
  v: string | null | undefined,
  detail?: string | null,
): string {
  if (!v) {
    return detail ? detail : "—";
  }
  const label = lookup(SOURCE_LABELS, v) ?? v;
  return detail ? `${label}: ${detail}` : label;
}

/** Labels for the stored "when did you last do a Hawan" answer. Mirrors
 *  LAST_HAWAN_OPTIONS in the backend's bundle.ts (kept local, as with
 *  SOURCE_LABELS, so the admin needs no runtime import for five words). */
const LAST_HAWAN_LABELS = {
  within_month: "Within a month",
  within_6_months: "Within 6 months",
  within_year: "Within a year",
  over_year: "More than a year ago",
  never: "Never",
} satisfies Record<string, string>;

/** The label to show for a stored answer; null when the form did not
 *  ask or the value is one we do not know, so callers render nothing. */
export function lastHawanLabel(
  value: string | null | undefined,
): string | null {
  return value ? (lookup(LAST_HAWAN_LABELS, value) ?? null) : null;
}

/** How many registrations said they have never done a Hawan: the people
 *  the organisers plan guidance for on the day. */
export function firstTimerCount(
  regs: readonly Pick<Registration, "lastHawan">[],
): number {
  return regs.filter((r) => r.lastHawan === "never").length;
}

/** How each currency is marked. Australian dollars stay a plain "$",
 *  as the Australian team reads them everywhere else. */
const CURRENCY_MARK = {
  aud: "$",
  nzd: "NZ$",
  sgd: "S$",
} satisfies Record<string, string>;

/**
 * Money from cents: "$250", "NZ$250", "S$51"; whole units unless there
 * are cents ("$12.50"). Defaults to this build's currency. Pass the
 * catalogue price's currency where there is one, so a Christchurch
 * booking reads NZ$ in the Australian build.
 */
export function formatMoney(
  cents: number,
  currency: string = CHAPTER.currency,
): string {
  const code = currency.toLowerCase();
  const mark = lookup(CURRENCY_MARK, code) ?? `${code.toUpperCase()} `;
  const digits = cents % 100 === 0 ? 0 : 2;
  const amount = new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Math.abs(cents) / 100);
  return `${cents < 0 ? "-" : ""}${mark}${amount}`;
}

/**
 * Payment shape of a focused roster, derived from the registrations
 * themselves — no server aggregation needed. An event is "paid" once any
 * of its registrations carries a `bookingType` (paid or interest), so
 * free events return `isPaid: false` and render none of the money UI.
 */
export interface PaidSummary {
  isPaid: boolean;
  collectedCents: number;
  paidCount: number;
  /** Bookings that registered interest but haven't paid yet. */
  interestCount: number;
}

export function paidSummaryOf(regs: readonly Registration[]): PaidSummary {
  const paid = regs.filter((r) => r.bookingType === "paid");
  const interestCount = regs.filter((r) => r.bookingType === "interest").length;
  const collectedCents = paid.reduce(
    (sum, r) => sum + (r.paidAmountCents ?? 0),
    0,
  );
  return {
    isPaid: paid.length > 0 || interestCount > 0,
    collectedCents,
    paidCount: paid.length,
    interestCount,
  };
}
